import { Pause, Play } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import { HlsAudioPlayer } from '../utils/HlsAudioPlayer';

interface PlayButtonProps {
  hlsAudioPlayer: HlsAudioPlayer;
}

export const PlayButton: React.FC<PlayButtonProps> = ({ hlsAudioPlayer }) => {
  const [isPlaying, setIsPlaying] = React.useState(hlsAudioPlayer.getState().isPlaying);
  const handlePlayPause = useCallback((event: String) => {
    setIsPlaying(event === "Playing");
  }, []);

  useEffect(() => {
    const handlePause = () => handlePlayPause("Paused");
    const handlePlay = () => handlePlayPause("Playing");
    const handleEnded = () => handlePlayPause("Ended");
    const handleError = (error: any) => handlePlayPause(`Error: ${error}`);

    hlsAudioPlayer.addEventListener('pause', handlePause);
    hlsAudioPlayer.addEventListener('play', handlePlay);
    hlsAudioPlayer.addEventListener('ended', handleEnded);
    hlsAudioPlayer.addEventListener('error', handleError);

    return () => {
      hlsAudioPlayer.removeEventListener('pause', handlePause);
      hlsAudioPlayer.removeEventListener('play', handlePlay);
      hlsAudioPlayer.removeEventListener('ended', handleEnded);
      hlsAudioPlayer.removeEventListener('error', handleError);
    }
  }, [hlsAudioPlayer, handlePlayPause]);

  const togglePlayPause = async () => {
    try {
      if (isPlaying) {
        await hlsAudioPlayer.pause();
      } else {
        await hlsAudioPlayer.resume();
      }
    } catch (error) {
      console.error('Toggle play/pause failed:', error);
    }
  };

  return (
    <button
      onClick={togglePlayPause}
      disabled={hlsAudioPlayer.getState().isBuffering}
      className="p-1.5 hover:bg-gray-200 rounded-full transition-colors"
      aria-label={hlsAudioPlayer.getState().isPlaying ? 'Pause' : 'Play'}
    >
      {isPlaying ? <Pause size={20} /> : <Play size={20} />}
    </button>
  );
};